import { isIosPlatform } from "@src/appV2/lib";
import { getDeviceInfo } from "@src/appV2/NativeAdapters/device.adapter";
import semVer from "semver";

const MIN_IOS_VERSION = "16.0.0";

/**
 * Determine whether the current device is running an iOS version that is no longer
 * supported by the Google Maps JavaScript SDK (v3.59+). Google officially supports
 * only “the current and previous major versions of iOS”, meaning iOS 16+ at the
 * time of writing. On iOS 15.x and below the SDK references `HTMLDialogElement`,
 * which does not exist in Safari — the script crashes before it finishes loading
 * and breaks our map component.
 *
 * References:
 * • Google bug tracker – https://issuetracker.google.com/issues/375937548
 * • Internal incident thread – see Slack link in ACT-2612
 *
 * We coerce the OS version string to semver so that `semver.lt()` can reliably
 * compare dotted versions (e.g. "15.2" → "15.2.0"). Non-iOS platforms always
 * return `false` because they are unaffected.
 */
/**
 * Returns `true` if the current device is iOS < 16 (unsupported), otherwise `false`.
 */
export const isUnsupportedIosVersionForGoogleMaps = async (): Promise<boolean> => {
  if (!isIosPlatform()) {
    return false;
  }

  const { osVersion } = await getDeviceInfo();

  const semanticVersion = semVer.coerce(osVersion);

  if (!semanticVersion) {
    return false;
  }

  return semVer.lt(semanticVersion, MIN_IOS_VERSION);
};
